import type React from 'react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Footprints,
  Lock,
  Flame,
  Columns3,
  Rows3,
  Grid3x3,
  Target,
  HelpCircle,
  ChevronDown,
  Gauge,
  Swords,
  Shield,
} from 'lucide-react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { TennisBall } from '@/assets/icons/TennisBall';
import type {
  SegmentStatistics,
  PlayerStatistics,
  PredictionMode,
} from '@/types/statistics';
import { playerCropToDataUri } from '@/lib/transforms';
import { SHOW_WINNERS } from '@/lib/constants';
import { cn } from '@/utils/cn';
import { formatDistance, formatSpeedKmh } from '@/lib/utils';
import { usePlayerNameResolver } from '@/contexts/PlayerNamesContext';
import OverviewPage from './OverviewPage';
import TeamHeader, { type HeaderTeam, type SelectMode } from './TeamHeader';
import SmartCourt, {
  shotMarkColor,
  type CourtMode,
  type CourtPlayer,
  type CourtSide,
} from './SmartCourt';
import { overviewCourtMode, courtShowsShots } from './courtModes';
import { assignPlayerColors, type PlayerColor } from './playerColors';
import { aggregateTeamStatistics } from './teamStats';
import {
  SHOT_TYPES,
  SHOT_TYPE_CATALOG,
  classifyShot,
  selectShotConsistency,
  selectShots,
} from './shotTypes';
import ShotSpeedBars, { type ShotSpeedCompareEntity } from './ShotSpeedBars';
import LobAnalysis, { type LobCompareEntity } from './LobAnalysis';
import {
  buildShotSpeedRows,
  buildLobGroupMetrics,
  type LobSelection,
  type ShotSpeedRow,
  type LobAnalytics,
} from './shotAnalytics';
import ShotSimulator from './ShotSimulator';
import {
  StyleDiverging,
  ConsistencyDiverging,
  type StyleDatum,
  type ConsistencyDatum,
} from './StyleOfPlay';
import ShotRadar, { type RadarDatum } from './ShotRadar';
import { shotRadarValuesForSource } from './tournamentMetrics';
import Donut from './Donut';
import {
  depthOccupancy,
  DEPTH_COLORS,
  type DepthKey,
  type DepthSlice,
} from './positionMetrics';
import { CompareGroup, type CmpDatum } from './CompareBars';
import { VersusBars, type VersusEntity } from './VersusBars';
import { InfoSheet } from './InfoSheet';
import { UnlockCta } from './LockedPreview';
import MatchlyticsScoreSection, {
  RaceFinals,
  ShotRaces,
  StepChart,
} from './ContributionIndexCard';
import { matchlyticsScore, speedSeries } from './contributionMetrics';
import { sampleMatchlyticsScore } from './upsellSample';
import { inDepthTabKeys, inDepthTabsShown } from './reportTabs';
import { useTour } from '../tour/TourContext';
import { selectionTarget } from '../tour/tourSelection';
import type { TourSelection } from '../tour/tourSteps';

interface TournamentReportProps {
  segment: SegmentStatistics;
  defaultPlayerId?: string;
  /** Theme/brand variant. 'fip' (default) shows the FIP chip + Torneio eyebrow;
      'report' hides the chip and shows a neutral eyebrow. */
  brand?: 'fip' | 'report';
  /** Gates shot-dependent UI. 'advanced' (default) shows everything. */
  predictionMode?: PredictionMode;
  /** Pre-built slot→image map (data URIs / avatar URLs). When omitted, crops
      are derived from segment.player_crops (tournament route). */
  crops?: Record<string, string>;
  /** Rendered at the very top of the sticky header slab (inside the colored
      band). The report route passes its block slider here so the blocks read
      as part of the header. */
  headerTop?: React.ReactNode;
  /** True on the carousel's active slide only: this instance's DOM anchors
      the guided tour and its tab setters drive the tour's tab switches.
      No-op without a TourProvider above (the /tournament route). */
  tourHost?: boolean;
}

type TabId = 'shots' | 'movement';
type MegaTab = 'overview' | 'indepth';
// The movement court can show the continuous heatmap or one of three occupancy
// divisions. The divisions need one player per half, so they're gated below.
type MoveView = 'heatmap' | 'sides' | 'frontback' | 'zones';
// The Shots court can show raw landing markers or one of two zone-analysis
// views (lateral = left/middle/right destination, depth = short/medium/long).
// The zone views need a single selected player, so they're gated below.
type ShotView = 'shots' | 'lateral' | 'depth';

/**
 * A selected entity to compare — either one player or one whole team. Both
 * carry a (real or aggregated) PlayerStatistics so the court and the
 * comparison cards treat them identically.
 */
interface CompareEntity {
  id: string;
  name: string;
  color: PlayerColor;
  crop?: string;
  /** Team entities have no single crop — the members' face crops go here so a
      team reads as its players (not just a color) in the comparison rows. */
  memberCrops?: string[];
  ps: PlayerStatistics;
  side: CourtSide;
  teamId: string;
}

// Esquerda/Direita is stored in each player's OWN perspective (their left/right
// as they face the net) and is already correct for BOTH teams — verified
// against the position data: every 'left' player sits on their own
// negative-lateral half, every 'right' on the positive, regardless of which
// court-half the team plays. So role labels and roster order are taken verbatim.
// The court instead flips the LATERAL axis per half (see latY/latYin in
// SmartCourt): the right-half team faces the other way, a 180° rotation that
// flips both depth and lateral, so own-left/right lands on the correct screen
// side. (The old code wrongly assumed camera-relative roles and flipped the
// label instead of the court — mirroring the right team everywhere.)
const normRole = (raw: string | undefined): 'left' | 'right' =>
  raw === 'right' ? 'right' : 'left';

/** Per-entity shot-type ratio maps (from shots_breakdown players/teams). Each
    map is internally normalized: offensive ratios sum to 1, defensive too. */
/** Net-dominance + synchronization metrics for one team (formation_analysis). */
interface FormationDatum {
  id: string;
  name: string;
  color: string;
  crops?: string[];
  // net dominance
  netTime: number; // 0..1 ratio
  conquests: number; // per rally
  hold: number; // seconds
  advance: number; // seconds
  // synchronization
  sync: number; // 0..1 ratio
  coverage: number; // 0..1 ratio
}

// ---- primitives ---------------------------------------------------------

// Outcome legend marker — the SAME shapes drawn on the court: a filled
// circle (bounce), a haloed filled circle (winner), a hollow circle
// (interception) and an X (error).
const OutcomeMarker: React.FC<{ cat: string; identity?: string }> = ({
  cat,
  identity,
}) => {
  // Same helper the court paints with, so a swatch is never a colour that
  // isn't on the field.
  const color = shotMarkColor(cat, identity);
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      {cat === 'bounce' && <circle cx="8" cy="8" r="5" fill={color} />}
      {/* Bigger and haloed, exactly as the court draws it. */}
      {cat === 'winner' && (
        <>
          <circle
            cx="8"
            cy="8"
            r="6.9"
            fill="none"
            stroke={color}
            strokeWidth="1.1"
          />
          <circle cx="8" cy="8" r="5" fill={color} />
        </>
      )}
      {cat === 'interception' && (
        <circle
          cx="8"
          cy="8"
          r="5"
          fill="none"
          stroke={color}
          strokeWidth="2"
        />
      )}
      {cat === 'error' && (
        <path
          d="M 4 4 L 12 12 M 4 12 L 12 4"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

// The outcomes, in the order the compact legend row lists them. `l` is the
// shared `label.*` key, so the glossary term always reads exactly like the
// count row above it. Winners come last and drop out wholesale when
// SHOW_WINNERS is off, leaving the original three untouched.
const OUTCOME_KEYS = (
  [
    { key: 'bounce', l: 'bounces' },
    { key: 'interception', l: 'intercept' },
    { key: 'error', l: 'errors' },
    { key: 'winner', l: 'winners' },
  ] as const
).filter((o) => SHOW_WINNERS || o.key !== 'winner');

// Collapsible glossary under the court's outcome counts — the compact row
// above can only afford an abbreviation ("No solo", "Intercept."), which
// doesn't say what actually happened to the ball. Closed by default, same
// quiet chrome as the pentagon's glossary; each row leads with the very mark
// it explains, so opening it once makes the court readable.
const OutcomeGlossary: React.FC = () => {
  const { t } = useTranslation('tournament');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const ease = [0.22, 1, 0.36, 1] as const;
  return (
    <div className="glossary">
      <button
        type="button"
        className="glossary-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <HelpCircle size={11.5} strokeWidth={2.2} />
        <span>{t('outcomeLegend.toggle')}</span>
        <motion.span
          className="glossary-chev"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.26, ease }}
        >
          <ChevronDown size={12} strokeWidth={2.2} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            id={panelId}
            className="glossary-reveal"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease }}
          >
            <ul className="glossary-list">
              {OUTCOME_KEYS.map(({ key, l }, i) => (
                <motion.li
                  key={key}
                  className="glossary-item"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.08 + i * 0.055,
                      duration: 0.28,
                      ease,
                    },
                  }}
                >
                  <span className="glossary-mark">
                    <OutcomeMarker cat={key} />
                  </span>
                  <span className="glossary-term">
                    {t(`label.${l}` as never) as string}
                  </span>
                  <span className="glossary-def">
                    {t(`outcomeLegend.${l}` as never) as string}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Eyebrow: React.FC<{ k: string; beta?: boolean }> = ({ k, beta }) => {
  const { t } = useTranslation('tournament');
  return (
    <div className="eyebrow">
      <span className="eyebrow-pt">{t(`label.${k}` as never)}</span>
      {beta ? <span className="eyebrow-beta">beta</span> : null}
    </div>
  );
};

// ---- tabs ---------------------------------------------------------------

const OFFENSIVE_CHIPS = SHOT_TYPE_CATALOG.filter(
  (shotType) => shotType.group === 'offensive',
);
const DEFENSIVE_CHIPS = SHOT_TYPE_CATALOG.filter(
  (shotType) => shotType.group === 'defensive',
);

/**
 * What a whole tab would hold, for a report that can't fill it.
 *
 * Deliberately no mocked charts here, unlike the Visão Geral card previews: a
 * tab is a destination the reader chose to open, so it answers in words rather
 * than making them decode a fogged graphic they can't use.
 */
const LockedTab: React.FC<{ description: string }> = ({ description }) => {
  const { t } = useTranslation();
  return (
    <div className="locked-tab">
      <span className="locked-tab-badge">
        <Lock size={19} strokeWidth={2} aria-hidden="true" />
      </span>
      <p className="locked-tab-desc">{description}</p>
      <UnlockCta className="locked-cta">
        {t('tournament:locked.cta' as never)}
      </UnlockCta>
    </div>
  );
};

const ShotsTab: React.FC<{
  players: CompareEntity[];
  shotType: string;
  setShotType: (s: string) => void;
  styleData?: StyleDatum[];
  radarData?: RadarDatum[];
  speedRows?: ShotSpeedRow[] | null;
  speedScope?: string;
  speedComparison?: ShotSpeedCompareEntity[];
  lobMetrics?: LobAnalytics | null;
  lobComparison?: LobCompareEntity[];
  lobVisible?: boolean;
  lobSelection: LobSelection;
  setLobSelection: (s: LobSelection) => void;
  /** The block: its clock places the error / winner charts' shots. */
  segment: SegmentStatistics;
  /** The entity drawn on top in those charts (the viewer, or the focus team). */
  focusId?: string;
}> = ({
  players,
  shotType,
  setShotType,
  styleData,
  radarData,
  speedRows,
  speedScope,
  speedComparison,
  lobMetrics,
  lobComparison,
  lobVisible,
  lobSelection,
  setLobSelection,
  segment,
  focusId,
}) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;

  // Accepted shots only — drops low-confidence detections so the counts match
  // the main report (see selectShots / transforms.ts).
  const shotsOf = (ps: PlayerStatistics, type: string) => selectShots(ps, type);

  // Chip counts are summed across selected entities.
  const chipCount = (type: string) =>
    players.reduce((sum, p) => sum + shotsOf(p.ps, type).length, 0);

  const hasShots = players.some((p) => p.ps.shots_tracking);
  if (!hasShots) {
    return (
      <div className="tab-content">
        <div className="tr-empty">{tl('noShotData')}</div>
      </div>
    );
  }

  const base = (p: CompareEntity) => ({
    id: p.id,
    name: p.name,
    crop: p.crop,
    crops: p.memberCrops,
    color: p.color.solid,
  });
  // In-play vs error, as a diverging pair (sums to 100%) per entity.
  const consistencyData: ConsistencyDatum[] = players.flatMap((p) => {
    const rates = selectShotConsistency(p.ps, shotType);
    return rates
      ? [
          {
            id: p.id,
            name: p.name,
            color: p.color.solid,
            crop: p.crop,
            crops: p.memberCrops,
            ...rates,
          },
        ]
      : [];
  });
  const volData: CmpDatum[] = players.map((p) => {
    const n = shotsOf(p.ps, shotType).length;
    return { ...base(p), value: n, display: String(n) };
  });

  const single = players.length <= 1;
  const hasStyle = !!styleData && styleData.length > 0;

  // The selected entities (players, or teams aggregated) as the block's
  // players, for the error / winner charts under Consistência.
  const raceSegment: SegmentStatistics = {
    ...segment,
    statistics: {
      ...segment.statistics,
      players_statistics: Object.fromEntries(players.map((p) => [p.id, p.ps])),
    },
  };
  const raceColors = Object.fromEntries(players.map((p) => [p.id, p.color]));
  const raceCrops = Object.fromEntries(
    players.map((p) => [p.id, p.crop ?? '']),
  );
  const raceNames = Object.fromEntries(players.map((p) => [p.id, p.name]));
  const raceMemberCrops = Object.fromEntries(
    players.map((p) => [p.id, p.memberCrops]),
  );
  // One stroke picked: how its average speed built up across the block.
  const speedRace =
    shotType === SHOT_TYPES[0].key
      ? null
      : speedSeries(
          raceSegment,
          players.map((p) => p.id),
          shotType,
        );

  return (
    <div className="tab-content">
      <div className="shot-filter" role="group" aria-label={tl('shotTypes')}>
        <button
          type="button"
          aria-pressed={shotType === SHOT_TYPES[0].key}
          data-tour-press={`chip-${SHOT_TYPES[0].key}`}
          className={`chip chip-all ${
            shotType === SHOT_TYPES[0].key ? 'active' : ''
          }`}
          onClick={() => setShotType(SHOT_TYPES[0].key)}
        >
          <span className="chip-label">
            {tl(SHOT_TYPES[0].lbl).toUpperCase()}
          </span>
          <span className="chip-num">{chipCount(SHOT_TYPES[0].key)}</span>
        </button>
        {/* The seven types split into their two groups — the same
            offensive/defensive language the radar and Estilo de Jogo use. It
            also makes the count land as a full 3 + 4 instead of leaving one
            orphan chip at the end of a three-column grid. */}
        {[
          { key: 'offensive', Icon: Swords, chips: OFFENSIVE_CHIPS, cols: 3 },
          { key: 'defensive', Icon: Shield, chips: DEFENSIVE_CHIPS, cols: 2 },
        ].map(({ key, Icon, chips, cols }) => (
          <div key={key} className="chip-group">
            <span className="chip-group-head">
              <Icon size={11} strokeWidth={2.2} aria-hidden />
              {tl(key)}
            </span>
            <div className={`chip-grid chip-grid-${cols}`}>
              {chips.map((s) => {
                const active = shotType === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    aria-pressed={active}
                    data-tour-press={`chip-${s.key}`}
                    className={`chip ${active ? 'active' : ''}`}
                    onClick={() => setShotType(s.key)}
                  >
                    <span className="chip-label">
                      {tl(s.lbl).toUpperCase()}
                    </span>
                    <span className="chip-num">{chipCount(s.key)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Total shots — the headline volume number, shown first. One hero stat
          for a single entity, comparison bars for several. */}
      <div className="card">
        <Eyebrow k="totalShots" />
        {single ? (
          <div className="total-hero">
            <TennisBall
              className="total-hero-icon"
              size={30}
              strokeWidth={1.6}
            />
            <span className="total-hero-num">{volData[0]?.display ?? '—'}</span>
          </div>
        ) : (
          <CompareGroup metrics={[{ label: '', data: volData }]} />
        )}
      </div>

      {/* The picked stroke's average ball speed, shot by shot across the
          block, over the shots with an accepted 3-D trajectory (beta, like the
          speed bars). */}
      {speedRace ? (
        <div className="card speed-evolution" data-tour="speed-evolution">
          <Eyebrow k="speedEvolution" beta />
          <StepChart
            data={speedRace}
            playerIds={players.map((p) => p.id)}
            colors={raceColors}
            focusPlayerId={focusId ?? ''}
            label={tl('speedEvolution')}
          />
          <RaceFinals
            data={speedRace}
            playerIds={players.map((p) => p.id)}
            colors={raceColors}
            crops={raceCrops}
            names={raceNames}
            memberCrops={raceMemberCrops}
            format={(v) => `${Math.round(v)} ${t('speed.unit')}`}
          />
        </div>
      ) : null}

      {/* Consistency — in-play vs error, diverging from 50% (same graph as
          Estilo de Jogo), the same for one entity or several. Then error rate,
          winner rate and winners per error run shot by shot (as in Visão
          Geral), for the selection and the chip. */}
      {consistencyData.length > 0 ? (
        <div className="card">
          <Eyebrow k="consistency" />
          <ConsistencyDiverging data={consistencyData} hideWinners />
          <ShotRaces
            segment={raceSegment}
            playerIds={players.map((p) => p.id)}
            colors={raceColors}
            crops={raceCrops}
            focusPlayerId={focusId}
            shotType={shotType}
            tour="indepth-races"
            names={raceNames}
            memberCrops={raceMemberCrops}
          />
        </div>
      ) : null}

      {/* Offensive vs defensive shot mix — diverging-from-50% bars, the same
          for one entity or several. Available for both players and teams. */}
      {hasStyle ? (
        <div className="card">
          <Eyebrow k="styleOfPlay" />
          <StyleDiverging data={styleData!} />
        </div>
      ) : null}

      {/* Shot-type breakdown — one pentagon, a colored blob per entity. */}
      {radarData && radarData.length > 0 ? (
        <div className="card">
          <Eyebrow k="shotTypes" />
          <ShotRadar data={radarData} />
        </div>
      ) : null}

      {/* Average speed per type: compact shared-scale rows for one player,
          per-entity comparison groups when several are selected. */}
      {speedRows || speedComparison ? (
        <div className="card">
          <Eyebrow k="shotSpeed" beta />
          <ShotSpeedBars
            rows={speedRows ?? []}
            scopeNote={speedScope}
            comparison={speedComparison}
          />
        </div>
      ) : null}

      {/* Lob Analysis — its own subtype selector, independent of the chip
          filter above. In Depth only. */}
      {lobVisible ? (
        <LobAnalysis
          metrics={lobMetrics ?? null}
          comparison={lobComparison}
          selection={lobSelection}
          onSelect={setLobSelection}
        />
      ) : null}
    </div>
  );
};

interface PositionDepthRow {
  id: string;
  name: string;
  color: string;
  depth: DepthSlice[];
}

const MovementTab: React.FC<{
  players: CompareEntity[];
  teamFormation?: FormationDatum[];
  positionDepth?: PositionDepthRow[];
}> = ({ players, teamFormation, positionDepth }) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;
  const [netDomInfo, setNetDomInfo] = useState(false);
  const [syncInfo, setSyncInfo] = useState(false);

  // Depth-occupancy donut (net / transition / back) — the same chart Visão
  // Geral uses for position, reused here instead of the court-zone hexagon.
  const depthLabel: Record<DepthKey, string> = {
    net: t('overview.position.net'),
    transition: t('overview.position.transition'),
    back: t('overview.position.back'),
  };
  const renderDepth = (depth: DepthSlice[]) => (
    <div className="position-depth is-standalone">
      <Donut
        segments={depth.map((s) => ({
          key: s.key,
          pct: s.pct,
          color: DEPTH_COLORS[s.key],
        }))}
      />
      <ul className="position-depth-legend">
        {depth.map((s) => (
          <li key={s.key} className="position-depth-item">
            <span
              className="position-depth-dot"
              style={{ background: DEPTH_COLORS[s.key] }}
            />
            <span className="position-depth-pct">{s.pct}%</span>
            <span className="position-depth-name">{depthLabel[s.key]}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const base = (p: CompareEntity) => ({
    id: p.id,
    name: p.name,
    crop: p.crop,
    crops: p.memberCrops,
    color: p.color.solid,
  });

  const mk = (
    pick: (ps: PlayerStatistics) => number,
    toNum: (v: number) => string,
    unit?: string,
  ): CmpDatum[] =>
    players.map((p) => {
      const v = pick(p.ps);
      return { ...base(p), value: v, display: toNum(v), unit };
    });

  const single = players.length <= 1;

  // Players → depth-occupancy donut. Only shown for a SINGLE selected player —
  // the donut reads as one player's net/transition/back split, so comparing
  // several would just stack near-identical rings with no head-to-head value.
  // Teams → net-dominance + sync head-to-heads instead.
  const positionCard =
    positionDepth && positionDepth.length === 1 ? (
      <div className="card">
        <Eyebrow k="position" />
        {renderDepth(positionDepth[0].depth)}
      </div>
    ) : null;

  // Net dominance + synchronization as head-to-head (Team A vs B) bars, each
  // with a "?" opening a metric-explanation sheet. Teams-only (formation data).
  const a = teamFormation?.[0];
  const b = teamFormation?.[1];
  const hasVersus = !!a && !!b;
  const ent = (f: FormationDatum): VersusEntity => ({
    name: f.name,
    color: f.color,
    crops: f.crops,
  });
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const secs = (v: number) => `${v.toFixed(1)}s`;

  const netDomCard = hasVersus ? (
    <div className="card">
      <div className="card-head">
        <Eyebrow k="netDom" />
        <button
          type="button"
          className="info-btn"
          onClick={() => setNetDomInfo(true)}
          aria-label={tl('netDom')}
        >
          <HelpCircle size={15} strokeWidth={2} />
        </button>
      </div>
      <VersusBars
        left={ent(a!)}
        right={ent(b!)}
        metrics={[
          {
            label: tl('netTime'),
            left: a!.netTime,
            right: b!.netTime,
            fmt: pct,
          },
          {
            label: tl('conquests'),
            left: a!.conquests,
            right: b!.conquests,
            fmt: (v) => v.toFixed(1),
          },
          { label: tl('netHold'), left: a!.hold, right: b!.hold, fmt: secs },
          {
            label: tl('advance'),
            left: a!.advance,
            right: b!.advance,
            fmt: secs,
          },
        ]}
      />
      <InfoSheet
        open={netDomInfo}
        onClose={() => setNetDomInfo(false)}
        title={tl('netDom')}
        metrics={[
          {
            name: tl('netTime'),
            description: tl('netTimeDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('conquests'),
            description: tl('conquestsDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('netHold'),
            description: tl('holdDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('advance'),
            description: tl('advanceDesc'),
            higherIsBetter: false,
          },
        ]}
      />
    </div>
  ) : null;

  const syncCard = hasVersus ? (
    <div className="card">
      <div className="card-head">
        <Eyebrow k="sync" />
        <button
          type="button"
          className="info-btn"
          onClick={() => setSyncInfo(true)}
          aria-label={tl('sync')}
        >
          <HelpCircle size={15} strokeWidth={2} />
        </button>
      </div>
      <VersusBars
        left={ent(a!)}
        right={ent(b!)}
        metrics={[
          { label: tl('depthSync'), left: a!.sync, right: b!.sync, fmt: pct },
          {
            label: tl('coverage'),
            left: a!.coverage,
            right: b!.coverage,
            fmt: pct,
          },
        ]}
      />
      <InfoSheet
        open={syncInfo}
        onClose={() => setSyncInfo(false)}
        title={tl('sync')}
        metrics={[
          {
            name: tl('depthSync'),
            description: tl('depthSyncDesc'),
            higherIsBetter: true,
          },
          {
            name: tl('coverage'),
            description: tl('coverageDesc'),
            higherIsBetter: true,
          },
        ]}
      />
    </div>
  ) : null;

  if (single) {
    const ps = players[0].ps;
    const dist = formatDistance(ps.running_distance);
    const sp = formatSpeedKmh(ps.mean_velocity);
    return (
      <div className="tab-content">
        <div className="card">
          <Eyebrow k="physical" />
          <div className="phys">
            <div className="phys-stat">
              <Footprints className="phys-icon" size={26} strokeWidth={1.8} />
              <div className="phys-text">
                <div className="phys-val">
                  {dist.value}
                  <span className="phys-unit">{dist.unit}</span>
                </div>
                <div className="phys-label">{tl('distance')}</div>
              </div>
            </div>
            <span className="phys-divider" aria-hidden="true" />
            <div className="phys-stat">
              <Gauge className="phys-icon" size={26} strokeWidth={1.8} />
              <div className="phys-text">
                <div className="phys-val">
                  {sp.value}
                  <span className="phys-unit">{sp.unit}</span>
                </div>
                <div className="phys-label">{tl('avgSpeed')}</div>
              </div>
            </div>
          </div>
        </div>

        {positionCard}
        {netDomCard}
        {syncCard}
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="card">
        <Eyebrow k="physical" />
        <CompareGroup
          metrics={[
            {
              label: tl('distance'),
              data: players.map((p) => {
                const d = formatDistance(p.ps.running_distance);
                return {
                  ...base(p),
                  value: p.ps.running_distance,
                  display: d.value,
                  unit: d.unit,
                };
              }),
            },
            {
              label: tl('avgSpeed'),
              data: mk(
                // Segment mean_velocity is already km/h (not m/s) — no ×3.6.
                (ps) => ps.mean_velocity,
                (v) => v.toFixed(1),
                'km/h',
              ),
            },
          ]}
        />
      </div>

      {positionCard}
      {netDomCard}
      {syncCard}
    </div>
  );
};

// Movement court-view selector: a full-width segmented control above the court
// with a sliding active thumb. Heat map is always available; the three division
// views are disabled when they can't be drawn (more than one selected player on
// a half). Rendered in players mode only — teams mode hides it entirely.
// Icons mirror how each view actually paints on the LANDSCAPE court: sides
// (lateral thirds) read as horizontal stripes, front/back (depth) as columns.
const MOVE_VIEWS: {
  key: MoveView;
  Icon: typeof Flame;
}[] = [
  { key: 'heatmap', Icon: Flame },
  { key: 'sides', Icon: Rows3 },
  { key: 'frontback', Icon: Columns3 },
  { key: 'zones', Icon: Grid3x3 },
];

const MoveViewSelector: React.FC<{
  view: MoveView;
  onChange: (v: MoveView) => void;
  divisionsEnabled: boolean;
}> = ({ view, onChange, divisionsEnabled }) => {
  const { t } = useTranslation('tournament');
  const effective = divisionsEnabled ? view : 'heatmap';
  return (
    <div className="court-view-tabs" role="tablist">
      {MOVE_VIEWS.map(({ key, Icon }) => {
        const disabled = key !== 'heatmap' && !divisionsEnabled;
        const active = effective === key;
        const label = t(`move.${key}` as never) as string;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            className={cn(
              'court-view-tab',
              active && 'active',
              disabled && 'disabled',
            )}
            disabled={disabled}
            aria-selected={active}
            title={disabled ? (t('move.disabledHint') as string) : label}
            onClick={() => onChange(key)}
          >
            {active && (
              <motion.span
                layoutId="moveview-thumb"
                className="court-view-thumb"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
            <Icon className="court-view-tab-icon" size={15} strokeWidth={2.1} />
            <span className="court-view-tab-label">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

// Shots court-view selector — mirrors MoveViewSelector. 'shots' is the raw
// landing map (always available); the two zone-analysis views need a single
// selected player (advanced data), so they're disabled otherwise.
const SHOT_VIEWS: { key: ShotView; Icon: typeof Flame }[] = [
  { key: 'shots', Icon: Target },
  { key: 'lateral', Icon: Rows3 },
  { key: 'depth', Icon: Columns3 },
];

const ShotViewSelector: React.FC<{
  view: ShotView;
  onChange: (v: ShotView) => void;
  zonesEnabled: boolean;
}> = ({ view, onChange, zonesEnabled }) => {
  const { t } = useTranslation('tournament');
  const effective = zonesEnabled ? view : 'shots';
  return (
    <div className="court-view-tabs" role="tablist">
      {SHOT_VIEWS.map(({ key, Icon }) => {
        const disabled = key !== 'shots' && !zonesEnabled;
        const active = effective === key;
        const label = t(`shotView.${key}` as never) as string;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            className={cn(
              'court-view-tab',
              active && 'active',
              disabled && 'disabled',
            )}
            disabled={disabled}
            aria-selected={active}
            title={disabled ? (t('shotView.disabledHint') as string) : label}
            onClick={() => onChange(key)}
          >
            {active && (
              <motion.span
                layoutId="shotview-thumb"
                className="court-view-thumb"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
            <Icon className="court-view-tab-icon" size={15} strokeWidth={2.1} />
            <span className="court-view-tab-label">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---- shell --------------------------------------------------------------

const TournamentReport: React.FC<TournamentReportProps> = ({
  segment,
  defaultPlayerId,
  brand = 'fip',
  predictionMode = 'advanced',
  crops: cropsProp,
  headerTop,
  tourHost = false,
}) => {
  const { t } = useTranslation(['tournament', 'common', 'report']);
  const tl = (k: string) => t(`tournament:label.${k}` as never) as string;
  const stats = segment.statistics;
  const resolveName = usePlayerNameResolver();

  const crops = useMemo(() => {
    if (cropsProp) return cropsProp;
    const out: Record<string, string> = {};
    for (const [pid, b64] of Object.entries(segment.player_crops)) {
      out[pid] = playerCropToDataUri(b64);
    }
    return out;
  }, [cropsProp, segment.player_crops]);

  const { teamLeft, teamRight } = useMemo(() => {
    const entries = Object.entries(stats.teams);
    const roles = stats.roles;
    // Order a team so its own-perspective LEFT player leads — verbatim from the
    // data, the same for both teams (the right team is no longer relabelled).
    const order = (players: string[]) =>
      [...players].sort((a, b) => {
        const ra = normRole(roles?.[a]?.primary_role);
        const rb = normRole(roles?.[b]?.primary_role);
        if (ra === 'left' && rb !== 'left') return -1;
        if (rb === 'left' && ra !== 'left') return 1;
        return 0;
      });
    const mk = (
      e: [string, { teammates: string[] }] | undefined,
    ): HeaderTeam => ({
      teamId: e?.[0] ?? '',
      players: order(e?.[1]?.teammates ?? []),
    });
    // Report mode is viewed by a specific player: always seat them on the left
    // court (Team A); the other team draws on the right half.
    if (brand === 'report' && defaultPlayerId) {
      const focusIdx = entries.findIndex(([, team]) =>
        team.teammates.includes(defaultPlayerId),
      );
      if (focusIdx > 0) {
        return {
          teamLeft: mk(entries[focusIdx]),
          teamRight: mk(entries[0]),
        };
      }
    }
    return { teamLeft: mk(entries[0]), teamRight: mk(entries[1]) };
  }, [stats.teams, stats.roles, brand, defaultPlayerId]);

  // Stable roster order: left team first, then right — drives color
  // assignment and keeps selected players in a consistent order.
  const rosterOrder = useMemo(
    () => [...teamLeft.players, ...teamRight.players],
    [teamLeft.players, teamRight.players],
  );
  const colors = useMemo(() => assignPlayerColors(rosterOrder), [rosterOrder]);

  // Visão Geral is scoped to the player viewing the report.
  // TODO(tournament→report migration): the real report modes know the selected
  // player; until tournament mode is folded into them, default to player1
  // (falling back to the first player on court if that id isn't present). Swap
  // this for the selected player when migrating.
  const overviewFocusId =
    defaultPlayerId && stats.players_statistics[defaultPlayerId]
      ? defaultPlayerId
      : stats.players_statistics.player1
        ? 'player1'
        : rosterOrder[0];

  // Team A = left, Team B = right. Reuse the same palette so the two teams
  // get the first two (distinct, muted) identity hues.
  const teamOrder = useMemo(
    () => [teamLeft.teamId, teamRight.teamId].filter(Boolean),
    [teamLeft.teamId, teamRight.teamId],
  );
  const teamColors = useMemo(() => assignPlayerColors(teamOrder), [teamOrder]);

  const sideById = useMemo(() => {
    const m: Record<string, CourtSide> = {};
    teamLeft.players.forEach((p) => (m[p] = 'left'));
    teamRight.players.forEach((p) => (m[p] = 'right'));
    return m;
  }, [teamLeft.players, teamRight.players]);

  // Own-perspective role per player (Esquerda/Direita as the player experiences
  // the court) for every label — taken verbatim from the data for both teams.
  // The court flips the lateral axis per half for physical placement, so this
  // own-perspective role is the map to use anywhere a side is shown as text.
  const displayRoleById = useMemo(() => {
    const m: Record<string, 'left' | 'right'> = {};
    for (const pid of Object.keys(sideById)) {
      m[pid] = normRole(stats.roles[pid]?.primary_role);
    }
    return m;
  }, [sideById, stats.roles]);
  const headerRoles = useMemo(() => {
    const m: Record<string, { primary_role: string }> = {};
    for (const [pid, r] of Object.entries(displayRoleById)) {
      m[pid] = { primary_role: r };
    }
    return m;
  }, [displayRoleById]);

  const teamKeyById = useMemo(() => {
    const m: Record<string, string> = {};
    teamLeft.players.forEach((p) => (m[p] = teamLeft.teamId));
    teamRight.players.forEach((p) => (m[p] = teamRight.teamId));
    return m;
  }, [teamLeft, teamRight]);

  const initialFocus =
    defaultPlayerId ??
    teamLeft.players[0] ??
    Object.keys(stats.players_statistics)[0];

  // The report is written for one player, so every comparison is against them:
  // their chip can be added to but never dropped. Tournament mode mounts
  // without a defaultPlayerId — no owner, so its selector stays free-form.
  const lockedPlayerId =
    defaultPlayerId && rosterOrder.includes(defaultPlayerId)
      ? defaultPlayerId
      : null;

  const [mode, setMode] = useState<SelectMode>('players');
  const [selected, setSelected] = useState<string[]>([initialFocus]);
  // Teams mode is single-select: you pick one focus team, but both teams are
  // always compared. The focus just leads the comparison (listed first).
  const [focusTeam, setFocusTeam] = useState<string>(teamOrder[0] ?? 'team1');
  const [megaTab, setMegaTab] = useState<MegaTab>('overview');
  // Switching the whole view jumps the page back to the top. The header is
  // sticky and already pinned there, so it reads as seamless — but it also
  // avoids a lurch: each slide's content drives the page height (inactive
  // slides are capped), so a tab switch resizes the document. Scrolled down,
  // the browser clamps the scroll mid-switch, which drags the sticky header
  // (bounded by its now-shorter slide) against its container's bottom edge
  // exactly while the roster avatars run their layout animation. Resetting to
  // the top first keeps the header pinned so the avatars animate cleanly.
  // Stable (the guided tour registers it as an imperative frame action); the
  // ref carries the current tab so the no-op guard doesn't tie the callback's
  // identity to the state.
  const megaTabRef = useRef(megaTab);
  megaTabRef.current = megaTab;
  const selectMegaTab = useCallback((next: MegaTab) => {
    if (next === megaTabRef.current) return;
    window.scrollTo({ top: 0 });
    setMegaTab(next);
  }, []);
  // Derived rather than hardcoded: a basic report has no shots tab to open
  // on, so taking the first available key is what keeps the default valid.
  const [tab, setTab] = useState<TabId>(inDepthTabKeys(predictionMode)[0]);
  const [shotType, setShotType] = useState('all');
  // Lob Analysis has its own subtype state — deliberately NOT bound to the
  // chip filter above it, so changing the general filter never silently
  // rewrites the lob court.
  const [lobSelection, setLobSelection] = useState<LobSelection>('both');
  const [moveView, setMoveView] = useState<MoveView>('heatmap');
  // Shots tab court view: raw landings vs lateral/depth zone analysis, plus the
  // picked origin zone (null → falls back to the player's busiest zone).
  const [shotView, setShotView] = useState<ShotView>('shots');
  const [selectedShotZone, setSelectedShotZone] = useState<string | null>(null);
  const [simOpen, setSimOpen] = useState(false);
  const isAdvanced = predictionMode === 'advanced';
  // The block's Matchlytics Score; a basic report (no shots) gets a sample of
  // it behind fog instead.
  const score = useMemo(
    () =>
      isAdvanced
        ? matchlyticsScore(segment, rosterOrder)
        : sampleMatchlyticsScore(segment, rosterOrder),
    [segment, isAdvanced, rosterOrder],
  );
  // The block carousel mounts several TournamentReport slides at once. The
  // sliding-thumb toggles use framer-motion layoutIds, which must be unique per
  // instance — without this, duplicate layoutIds across slides collide and the
  // active thumb only paints in one slide (e.g. Visão Geral's "Posições" tab
  // shows white text on no background). Namespace them per instance.
  const layoutGroupId = useId();

  // Guided tour: the active report slide hands the tour its DOM root (target
  // lookups stay scoped to this instance), the tab switches and the roster
  // selection. useTour() is null on the /tournament route, which has no
  // TourProvider.
  //
  // The frame must never change identity — it's a dependency of the tour's
  // step choreography, so a frame rebuilt on every tab switch would restart
  // the very step that caused the switch, mid-move. So the setters are all
  // stable and the getters read refs rather than closing over state.
  const tour = useTour();
  const registerTourFrame = tour?.registerFrame;
  const rootRef = useRef<HTMLDivElement>(null);

  const tabRef = useRef(tab);
  tabRef.current = tab;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const rosterRef = useRef({
    order: rosterOrder,
    teamOf: teamKeyById,
    focusId: initialFocus,
  });
  rosterRef.current = {
    order: rosterOrder,
    teamOf: teamKeyById,
    focusId: initialFocus,
  };

  const getMegaTab = useCallback(() => megaTabRef.current, []);
  const getInDepthTab = useCallback(() => tabRef.current, []);
  const getMode = useCallback(() => modeRef.current, []);
  const getSelection = useCallback(() => selectedRef.current, []);
  const shotTypeRef = useRef(shotType);
  shotTypeRef.current = shotType;
  const getShotType = useCallback(() => shotTypeRef.current, []);
  const tourSelectionTarget = useCallback(
    (kind: TourSelection) => selectionTarget(kind, rosterRef.current),
    [],
  );
  // The tour only ever demonstrates the players selector, so a replay that
  // starts from teams mode lands back on players first.
  const setTourSelection = useCallback((ids: string[]) => {
    setMode('players');
    setSelected(ids.length ? ids : [rosterRef.current.focusId]);
  }, []);

  useEffect(() => {
    if (!tourHost || !registerTourFrame || !rootRef.current) return;
    return registerTourFrame({
      root: rootRef.current,
      getMegaTab,
      setMegaTab: selectMegaTab,
      getInDepthTab,
      setInDepthTab: setTab,
      getMode,
      setMode,
      selectionTarget: tourSelectionTarget,
      getSelection,
      setSelection: setTourSelection,
      getShotType,
      setShotType,
    });
  }, [
    tourHost,
    registerTourFrame,
    selectMegaTab,
    getMegaTab,
    getInDepthTab,
    getMode,
    setMode,
    tourSelectionTarget,
    getSelection,
    setTourSelection,
    getShotType,
  ]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      if (prev.includes(id)) {
        if (id === lockedPlayerId) return prev; // the report's owner is always compared
        if (prev.length === 1) return prev; // always keep at least one
        return prev.filter((x) => x !== id);
      }
      return rosterOrder.filter((p) => prev.includes(p) || p === id);
    });

  // Whole roster ⇄ just the report's owner, so one control covers both ends of
  // the comparison. Collapsing falls back to the initial focus in tournament
  // mode, which has no owner to return to.
  const toggleAll = () =>
    setSelected((prev) =>
      rosterOrder.every((id) => prev.includes(id))
        ? [lockedPlayerId ?? initialFocus]
        : rosterOrder,
    );

  const playersOfTeam = (teamId: string) =>
    teamId === teamLeft.teamId ? teamLeft.players : teamRight.players;

  const playerEntities: CompareEntity[] = useMemo(
    () =>
      rosterOrder
        .filter((id) => selected.includes(id) && stats.players_statistics[id])
        .map((id) => ({
          id,
          name: resolveName(id),
          color: colors[id],
          crop: crops[id],
          ps: stats.players_statistics[id],
          side: sideById[id],
          teamId: teamKeyById[id],
        })),
    [
      rosterOrder,
      selected,
      stats.players_statistics,
      resolveName,
      colors,
      crops,
      sideById,
      teamKeyById,
    ],
  );

  // Always both teams (focus first), so the cards/court always compare A vs B.
  const teamEntities: CompareEntity[] = useMemo(() => {
    const ordered = [
      focusTeam,
      ...teamOrder.filter((tid) => tid !== focusTeam),
    ];
    return ordered
      .filter((tid) => teamOrder.includes(tid))
      .map((tid) => {
        const members = playersOfTeam(tid).filter(
          (pid) => stats.players_statistics[pid],
        );
        return {
          id: tid,
          name: tid === teamLeft.teamId ? tl('teamA') : tl('teamB'),
          color: teamColors[tid],
          crop: undefined,
          memberCrops: members.map((pid) => crops[pid]).filter(Boolean),
          ps: aggregateTeamStatistics(
            members.map((pid) => stats.players_statistics[pid]),
          ),
          side: (tid === teamLeft.teamId ? 'left' : 'right') as CourtSide,
          teamId: tid,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    focusTeam,
    teamOrder,
    stats.players_statistics,
    teamColors,
    teamLeft.teamId,
    teamLeft.players,
    teamRight.players,
  ]);

  const entities = mode === 'teams' ? teamEntities : playerEntities;

  const primary = entities[0];
  const single = entities.length === 1;
  const isTeams = mode === 'teams';

  // Speed/lob analytics read the ORIGINAL player records. Each displayed
  // entity gets its own group; teams aggregate only their own members.
  const analyticsGroups = useMemo(
    () =>
      entities.map((entity) => ({
        id: entity.id,
        playerIds: (isTeams ? playersOfTeam(entity.id) : [entity.id]).filter(
          (pid) => !!stats.players_statistics[pid],
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entities, isTeams, stats.players_statistics, teamLeft, teamRight],
  );

  const speedGroups = useMemo(
    () =>
      analyticsGroups.map((group) => ({
        id: group.id,
        rows: buildShotSpeedRows(group.playerIds, stats.players_statistics),
      })),
    [analyticsGroups, stats.players_statistics],
  );

  const speedRows = single ? speedGroups[0]?.rows : null;
  const speedComparisonEntities: ShotSpeedCompareEntity[] = !single
    ? speedGroups
        .map((group, index) => ({
          id: group.id,
          name: entities[index].name,
          color: entities[index].color.solid,
          crop: entities[index].crop,
          crops: entities[index].memberCrops,
          rows: group.rows,
        }))
        .filter((entity) => entity.rows)
    : [];
  const speedComparison = speedComparisonEntities.length
    ? speedComparisonEntities
    : undefined;
  const speedScope = single
    ? entities[0].name
    : (t(
        isTeams
          ? 'tournament:speed.compareTeams'
          : 'tournament:speed.comparePlayers',
      ) as string);

  // Lobs are NEVER pooled across entities: one group per displayed entity, and
  // a team group carries only its own members. A single group keeps the
  // spatial court; two or more become per-row comparison pills.
  const lobGroups = analyticsGroups;

  // "Both" doubles as the card's existence test: it already falls back to the
  // subtype records when a player carries no combined one.
  const lobBothGroups = useMemo(
    () => buildLobGroupMetrics(lobGroups, stats.players_statistics, 'both'),
    [lobGroups, stats.players_statistics],
  );
  const lobSelectedGroups = useMemo(
    () =>
      lobSelection === 'both'
        ? lobBothGroups
        : buildLobGroupMetrics(
            lobGroups,
            stats.players_statistics,
            lobSelection,
          ),
    [lobBothGroups, lobGroups, lobSelection, stats.players_statistics],
  );

  const lobVisible = lobBothGroups.some((group) => group.metrics?.hasData);
  const lobMetrics =
    lobGroups.length === 1 ? lobSelectedGroups[0].metrics : null;
  const lobComparison: LobCompareEntity[] | undefined =
    lobGroups.length > 1
      ? lobSelectedGroups.map((group, index) => ({
          id: group.id,
          name: entities[index].name,
          color: entities[index].color.solid,
          crop: entities[index].crop,
          crops: entities[index].memberCrops,
          metrics: group.metrics,
        }))
      : undefined;

  // The simulator is a focused single-player view: close it whenever the
  // surrounding context changes (mega-tab, movement/shots tab, players/teams
  // mode) or the selection stops being exactly one player.
  useEffect(() => {
    setSimOpen(false);
  }, [megaTab, tab, mode]);
  useEffect(() => {
    if (!single || isTeams) setSimOpen(false);
  }, [single, isTeams]);

  // No basic-mode bounce off the shots tab any more: it's selectable there and
  // opens onto LockedTab, which is the whole point of showing it. The tab that
  // OPENS is still movement, via inDepthTabKeys seeding useState above.

  // Offensive/defensive split per selected entity. The ratios live in
  // shots_breakdown — per team under `teams[id]`, per player under
  // `players[id].summary` — so the same card serves both modes. Entities
  // without a breakdown are dropped; an empty result hides the card.
  const styleData: StyleDatum[] | undefined = useMemo(() => {
    const sb = stats.shots_breakdown as
      | {
          teams?: Record<
            string,
            { offensive_total_ratio?: number; defensive_total_ratio?: number }
          >;
          players?: Record<
            string,
            {
              summary?: {
                offensive_total_ratio?: number;
                defensive_total_ratio?: number;
              };
            }
          >;
        }
      | null
      | undefined;
    if (!sb) return undefined;
    const rows = entities
      .map((e): StyleDatum | null => {
        const ratios = isTeams ? sb.teams?.[e.id] : sb.players?.[e.id]?.summary;
        if (!ratios || typeof ratios.offensive_total_ratio !== 'number') {
          return null;
        }
        return {
          id: e.id,
          name: e.name,
          color: e.color.solid,
          crop: e.crop,
          crops: e.memberCrops,
          offensive: ratios.offensive_total_ratio,
          defensive: ratios.defensive_total_ratio ?? 0,
        };
      })
      .filter((r): r is StyleDatum => r !== null);
    return rows.length ? rows : undefined;
  }, [isTeams, stats.shots_breakdown, entities]);

  // Per-shot-type mix per entity, for the radar. Each axis is the type's share
  // WITHIN its group — offensive axes use offensive_shots_ratio and defensive
  // axes use defensive_shots_ratio. Taken
  // straight from the JSON, so the values match the main report's Shot Analysis
  // and the blob fills the chart instead of clustering near the center.
  const radarData: RadarDatum[] | undefined = useMemo(() => {
    const sb = stats.shots_breakdown as
      | {
          teams?: Record<
            string,
            {
              offensive_shots_ratio?: Record<string, number>;
              defensive_shots_ratio?: Record<string, number>;
            }
          >;
          players?: Record<
            string,
            {
              offensive_shots_ratio?: Record<string, number>;
              defensive_shots_ratio?: Record<string, number>;
            }
          >;
        }
      | null
      | undefined;
    if (!sb) return undefined;
    const rows = entities
      .map((e): RadarDatum | null => {
        const src = isTeams ? sb.teams?.[e.id] : sb.players?.[e.id];
        if (!src) return null;
        const values = shotRadarValuesForSource(src);
        if (values.every((v) => v <= 0)) return null;
        return {
          id: e.id,
          name: e.name,
          color: e.color.solid,
          crop: e.crop,
          crops: e.memberCrops,
          values,
        };
      })
      .filter((r): r is RadarDatum => r !== null);
    return rows.length ? rows : undefined;
  }, [isTeams, stats.shots_breakdown, entities]);

  const teamFormation: FormationDatum[] | undefined = useMemo(() => {
    if (!isTeams) return undefined;
    const fa = stats.formation_analysis as
      | Record<
          string,
          {
            formation_analysis: {
              net_dominance: {
                net_time_ratio: number;
                net_conquests_per_rally: number;
                avg_net_hold_duration: number;
                avg_back_to_net_duration: number;
              };
              team_synchronization: {
                depth_sync_score: number;
                lateral_coverage_ratio: number;
              };
            };
          }
        >
      | null
      | undefined;
    if (!fa) return undefined;
    const rows = entities
      .map((e): FormationDatum | null => {
        const f = fa[e.id]?.formation_analysis;
        if (!f) return null;
        return {
          id: e.id,
          name: e.name,
          color: e.color.solid,
          crops: e.memberCrops,
          netTime: f.net_dominance.net_time_ratio,
          conquests: f.net_dominance.net_conquests_per_rally,
          hold: f.net_dominance.avg_net_hold_duration,
          advance: f.net_dominance.avg_back_to_net_duration,
          sync: f.team_synchronization.depth_sync_score,
          coverage: f.team_synchronization.lateral_coverage_ratio,
        };
      })
      .filter((r): r is FormationDatum => r !== null)
      // Stable head-to-head order: Team A (left side) always first.
      .sort((a) => (a.id === teamLeft.teamId ? -1 : 1));
    return rows.length ? rows : undefined;
  }, [isTeams, stats.formation_analysis, entities, teamLeft.teamId]);

  // Players: net / transition / back depth occupancy for the donut (one row per
  // selected player; teams mode uses the net-dominance/sync head-to-heads).
  const positionDepth: PositionDepthRow[] | undefined = useMemo(() => {
    if (isTeams) return undefined;
    const rows = entities
      .map((e): PositionDepthRow | null => {
        const fb = e.ps.court_zone?.front_back;
        if (!fb) return null;
        const depth = depthOccupancy(fb);
        if (!depth) return null;
        return { id: e.id, name: e.name, color: e.color.solid, depth };
      })
      .filter((r): r is PositionDepthRow => r !== null);
    return rows.length ? rows : undefined;
  }, [isTeams, entities]);

  // Occupancy divisions need one selected player per half — i.e. every selected
  // player on a distinct side (1 player, or 2 from different teams). Same-team
  // pair, 3+ players, or teams mode → divisions can't be drawn.
  const divisionsEnabled =
    !isTeams && new Set(entities.map((e) => e.side)).size === entities.length;
  const effectiveMoveView: MoveView = divisionsEnabled ? moveView : 'heatmap';

  // Shot zone-analysis (Pancadas) only makes sense for ONE player and needs the
  // advanced per-zone distribution. Otherwise the two zone views are disabled
  // and we fall back to the raw shot-landing map.
  const shotZonesEnabled =
    !isTeams &&
    single &&
    !!primary?.ps.shots_tracking?.shot_distribution_by_player_zone;
  const effectiveShotView: ShotView = shotZonesEnabled ? shotView : 'shots';

  // Visão Geral court: the focus player's own map — movement heat + (advanced)
  // shot landings.
  const focusPs = stats.players_statistics[overviewFocusId];

  const courtMode: CourtMode =
    megaTab === 'overview'
      ? overviewCourtMode(isAdvanced)
      : tab === 'shots'
        ? effectiveShotView === 'lateral'
          ? 'shotLateral'
          : effectiveShotView === 'depth'
            ? 'shotDepth'
            : 'shots'
        : effectiveMoveView === 'heatmap'
          ? 'movement'
          : effectiveMoveView;

  // Overview has no shot filter — its combined court always shows every shot.
  const courtShotType = megaTab === 'overview' ? 'all' : shotType;

  // Visão Geral: just the focus player, lit, so the court draws their movement
  // heat (own half) + shot landings (opponent half) alone.
  const overviewFocusCourtPlayers: CourtPlayer[] = useMemo(
    () =>
      focusPs
        ? [
            {
              id: overviewFocusId,
              name: resolveName(overviewFocusId),
              color: colors[overviewFocusId],
              crop: crops[overviewFocusId],
              ps: focusPs,
              side: sideById[overviewFocusId],
              role: stats.roles[overviewFocusId]?.primary_role,
              dimmed: false,
            },
          ]
        : [],
    [
      focusPs,
      overviewFocusId,
      resolveName,
      colors,
      crops,
      sideById,
      stats.roles,
    ],
  );

  // In Depth, teams mode dims the non-focus team; players mode shows every
  // selected entity at full strength.
  const courtPlayers: CourtPlayer[] =
    megaTab === 'overview'
      ? overviewFocusCourtPlayers
      : entities.map((e) => ({
          ...e,
          dimmed: isTeams && e.teamId !== focusTeam,
        }));

  // Outcome legend counts for the shots actually painted on the court (filled
  // bounce / hollow interception / X error). Summed across whoever the court is
  // drawing, for the active shot filter. Only meaningful when shots are shown.
  // One tally per entity on court, each carrying the very colours that
  // entity's marks are painted in. With several selected, a single pooled
  // legend can't say whose marks are whose — and its swatches would show the
  // untinted outcome colour, which matches nothing on the field.
  const courtOutcomeTallies = useMemo(() => {
    if (!courtShowsShots(courtMode))
      return [] as {
        id: string;
        name: string;
        identity?: string;
        counts: Record<string, number>;
      }[];
    const multi = courtPlayers.length > 1;
    return courtPlayers.map((p) => {
      const counts: Record<string, number> = {
        bounce: 0,
        winner: 0,
        interception: 0,
        error: 0,
      };
      selectShots(p.ps, courtShotType).forEach((s) => {
        counts[classifyShot(s)]++;
      });
      return {
        id: p.id,
        name: p.name,
        identity: multi ? p.color.solid : undefined,
        counts,
      };
    });
  }, [courtPlayers, courtShotType, courtMode]);

  type TabIcon = React.ComponentType<{ size?: number; strokeWidth?: number }>;
  const TAB_ICONS: Record<TabId, TabIcon> = {
    movement: Footprints as TabIcon,
    shots: TennisBall,
  };
  const tabs = inDepthTabsShown(predictionMode).map(({ key, locked }) => ({
    key: key as TabId,
    locked,
    Icon: TAB_ICONS[key as TabId],
  }));

  if (!primary) return null;

  // The court header names the single selected entity; comparing several, it
  // shows the identity dots + a "comparing N" line instead.
  const singleSub = isTeams
    ? playersOfTeam(primary.teamId)
        .map((pid) => resolveName(pid))
        .join(' · ')
    : `${
        // Always the player's own-perspective role (Esquerda/Direita as the
        // player experiences the court), regardless of which half we draw.
        displayRoleById[primary.id] === 'right'
          ? tl('roleRight')
          : tl('roleLeft')
      } · ${primary.teamId === teamLeft.teamId ? tl('teamA') : tl('teamB')}`;

  return (
    <LayoutGroup id={layoutGroupId}>
      <div className="tr-root" ref={rootRef}>
        <div className="tr-shell">
          {/* Shell header: brand eyebrow + the two top-level mega-tabs. The
            player/team selector only appears in the In Depth tab — Visão Geral
            is player-agnostic, so there's nothing to select there. */}
          {/* Full-bleed sticky bar: .tr-shell caps the report at 430px and
            centers it, so once the header sticks the navy bar would show light
            gutters beside it. marginInline pulls the bar out to the viewport
            edges (no-op on phones ≤430, where the calc resolves to 0); the inner
            wrapper keeps the header content aligned with the 430px body column. */}
          <header
            className="sticky top-0 z-20 bg-[color:var(--fip-navy)]"
            style={{ marginInline: 'calc(50% - 50vw)' }}
          >
            <div className="mx-auto w-full max-w-[430px]">
              {/* FIP brand eyebrow only on the tournament route. The report route
              omits it — its top row is the block slider (passed in as
              headerTop), so the header reads as one cohesive slab. */}
              {brand === 'fip' && (
                <div className="flex items-center justify-center gap-2 px-3 pb-1.5 pt-2.5">
                  <span className="bg-white px-1.5 py-0.5 text-[9px] font-bold tracking-[0.12em] text-[color:var(--fip-navy)]">
                    FIP
                  </span>
                  <span
                    className="text-[9.5px] uppercase tracking-[0.32em] text-white/70"
                    style={{ fontFamily: 'var(--f-label)' }}
                  >
                    {t('tournament:eyebrow')}
                  </span>
                </div>
              )}

              {headerTop}

              {/* The roster is always shown. In Visão Geral it's a read-only table
              (rail collapsed, no hint, every player lit); In Depth turns it
              interactive and the rail + hint animate in. */}
              <div data-tour="roster">
                <TeamHeader
                  interactive={megaTab === 'indepth'}
                  mode={mode}
                  onModeChange={setMode}
                  teamLeft={teamLeft}
                  teamRight={teamRight}
                  playerCrops={crops}
                  selectedIds={megaTab === 'indepth' ? selected : rosterOrder}
                  colors={colors}
                  roles={headerRoles}
                  onToggle={toggle}
                  lockedPlayerId={lockedPlayerId}
                  onToggleAll={toggleAll}
                  focusTeamId={focusTeam}
                  teamColors={teamColors}
                  onSelectTeam={setFocusTeam}
                />
              </div>

              {/* Mega-tabs anchored at the bottom of the header. On the tour
                  host a replay "?" sits at the right edge (with a mirror
                  spacer so the tab centers stay put). */}
              <nav className="flex" data-tour="mega-tabs">
                {tour && tourHost ? (
                  <span className="w-9" aria-hidden="true" />
                ) : null}
                {(['overview', 'indepth'] as MegaTab[]).map((key) => {
                  const active = megaTab === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-tour-press={`mega-${key}`}
                      onClick={() => selectMegaTab(key)}
                      aria-pressed={active}
                      className={cn(
                        'relative flex-1 py-2.5 text-center text-[12px] uppercase tracking-[0.18em] outline-none transition-colors',
                        active
                          ? 'text-white'
                          : 'text-white/40 hover:text-white/75',
                      )}
                      style={{ fontFamily: 'var(--f-label)', fontWeight: 600 }}
                    >
                      {t(`tournament:mega.${key}` as never) as string}
                      {active && (
                        <span className="absolute inset-x-0 bottom-0 h-[3px] bg-[color:var(--fip-cyan)]" />
                      )}
                    </button>
                  );
                })}
                {tour && tourHost ? (
                  <button
                    type="button"
                    onClick={tour.start}
                    aria-label={t('report:tour.replay')}
                    className="flex w-9 items-center justify-center text-white/45 transition-colors active:text-white/85"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                ) : null}
              </nav>
            </div>
          </header>

          {/* Court + tabs region. The simulator replaces this whole block (the
            roster header above stays put), so the field rotates into place and
            the Movimento/Pancadas tabs drop away while it's active. */}
          <AnimatePresence mode="wait">
            {simOpen && primary ? (
              <motion.div key="sim" className="tr-swap">
                <ShotSimulator
                  player={primary}
                  subtitle={singleSub}
                  shotType={shotType}
                  setShotType={setShotType}
                  onClose={() => setSimOpen(false)}
                />
              </motion.div>
            ) : (
              <motion.div
                key="normal"
                className="tr-swap"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Visão Geral opens with the block's Matchlytics Score,
                    before its court. */}
                {megaTab === 'overview' && score ? (
                  <MatchlyticsScoreSection
                    score={score}
                    playerIds={rosterOrder}
                    colors={colors}
                    crops={crops}
                    focusPlayerId={overviewFocusId}
                    locked={!isAdvanced}
                  />
                ) : null}

                <section className="court-section" data-tour="court">
                  <div className="court-header">
                    {megaTab === 'overview' ? (
                      <>
                        <div className="court-header-left">
                          <div className="court-header-name">
                            {resolveName(overviewFocusId).toUpperCase()}
                          </div>
                          <div className="court-header-sub">
                            {isAdvanced
                              ? t('tournament:overview.court.playerSub')
                              : t('tournament:overview.court.playerSubBasic')}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="court-header-left">
                          {single ? (
                            <>
                              <div className="court-header-name">
                                {primary.name.toUpperCase()}
                              </div>
                              <div className="court-header-sub">
                                {singleSub}
                              </div>
                            </>
                          ) : (
                            <div className="court-header-sub">
                              {
                                t(
                                  `tournament:label.${
                                    isTeams ? 'comparingTeams' : 'comparing'
                                  }` as never,
                                  { count: entities.length },
                                ) as string
                              }
                            </div>
                          )}
                        </div>
                        {/* Shots: static filter label. (The Simulador launcher
                    is hidden for now — kept dormant in the code below until the
                    feature ships.) */}
                        {tab === 'shots' && (
                          <div className="court-header-mode">
                            {t(`tournament:tabs.${tab}` as never) as string}
                            {shotType !== 'all' && (
                              <>
                                {' '}
                                ·{' '}
                                {tl(
                                  SHOT_TYPES.find((s) => s.key === shotType)
                                    ?.lbl ?? 'all',
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Movement view selector — its own animated row. Hidden in teams
              mode and on non-movement tabs; expands/collapses smoothly so the
              court glides rather than jumps when switching tabs. */}
                  <AnimatePresence initial={false}>
                    {megaTab === 'indepth' &&
                      tab === 'movement' &&
                      !isTeams && (
                        <motion.div
                          key="moveview-row"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.26, ease: 'easeOut' }}
                          style={{ overflow: 'hidden' }}
                        >
                          <MoveViewSelector
                            view={moveView}
                            onChange={setMoveView}
                            divisionsEnabled={divisionsEnabled}
                          />
                        </motion.div>
                      )}
                  </AnimatePresence>

                  {/* Shots view selector — mirrors the movement row: raw shot
              landings vs the lateral / depth zone analysis. */}
                  <AnimatePresence initial={false}>
                    {megaTab === 'indepth' && tab === 'shots' && !isTeams && (
                      <motion.div
                        key="shotview-row"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.26, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <ShotViewSelector
                          view={shotView}
                          onChange={setShotView}
                          zonesEnabled={shotZonesEnabled}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <SmartCourt
                    mode={courtMode}
                    players={courtPlayers}
                    shotType={courtShotType}
                    selectedShotZone={selectedShotZone}
                    onSelectShotZone={setSelectedShotZone}
                  />

                  {/* Outcome legend — sits directly under the court and only
                  when the court is actually painting shot markers; the shapes
                  match the ones drawn on the field. Expands/collapses smoothly
                  so it glides in and out as tabs change. */}
                  <AnimatePresence initial={false}>
                    {courtShowsShots(courtMode) && (
                      <motion.div
                        key="court-legend"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.26, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        {/* One legend per entity on court. With a single one
                            the owner label is redundant (the court header
                            already names them), so it reads exactly as before. */}
                        {courtOutcomeTallies.map((tally) => (
                          <div key={tally.id} className="court-legend-block">
                            {courtOutcomeTallies.length > 1 && (
                              <div
                                className="court-legend-owner"
                                style={{ color: tally.identity }}
                              >
                                {tally.name}
                              </div>
                            )}
                            <div className="legend-row court-legend-row">
                              {OUTCOME_KEYS.map(({ key, l }) => (
                                <div key={key} className="legend-item">
                                  <OutcomeMarker
                                    cat={key}
                                    identity={tally.identity}
                                  />
                                  <span className="legend-text">{tl(l)}</span>
                                  <span className="legend-num">
                                    {tally.counts[key]}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        <OutcomeGlossary />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>

                {megaTab === 'overview' ? (
                  <OverviewPage
                    segment={segment}
                    crops={crops}
                    colors={colors}
                    focusPlayerId={overviewFocusId}
                    isAdvanced={isAdvanced}
                  />
                ) : (
                  <>
                    {/* Tab bar */}
                    {tabs.length > 1 && (
                      <nav className="tab-bar" data-tour="indepth-tabs">
                        {tabs.map(({ key, Icon, locked }) => (
                          <button
                            key={key}
                            data-tour-press={`indepth-${key}`}
                            className={`tab ${tab === key ? 'active' : ''}${
                              locked ? ' is-locked' : ''
                            }`}
                            onClick={() => setTab(key)}
                          >
                            <Icon size={18} strokeWidth={1.7} />
                            <span>
                              {(
                                t(`tournament:tabs.${key}` as never) as string
                              ).toUpperCase()}
                            </span>
                            {/* Locked tabs still open — onto the pitch for
                                what they hold — so the lock is a hint, not a
                                barrier. */}
                            {locked && (
                              <Lock
                                size={11}
                                strokeWidth={2.6}
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        ))}
                      </nav>
                    )}

                    {/* Tab content */}
                    <main className="content">
                      {tab === 'shots' && !isAdvanced && (
                        <LockedTab
                          description={t(
                            'tournament:locked.tab.shots' as never,
                          )}
                        />
                      )}
                      {tab === 'shots' && isAdvanced && (
                        <ShotsTab
                          players={entities}
                          shotType={shotType}
                          setShotType={setShotType}
                          styleData={styleData}
                          radarData={radarData}
                          speedRows={speedRows}
                          speedScope={speedScope}
                          speedComparison={speedComparison}
                          lobMetrics={lobMetrics}
                          lobComparison={lobComparison}
                          lobVisible={lobVisible}
                          lobSelection={lobSelection}
                          setLobSelection={setLobSelection}
                          segment={segment}
                          focusId={
                            isTeams ? focusTeam : lockedPlayerId ?? undefined
                          }
                        />
                      )}
                      {tab === 'movement' && (
                        <MovementTab
                          players={entities}
                          teamFormation={teamFormation}
                          positionDepth={positionDepth}
                        />
                      )}
                    </main>
                  </>
                )}

                <footer className="footer">
                  <div className="footer-divider" />
                  <div className="footer-text">{t('common:poweredBy')}</div>
                  <img src="/icons/logo_with_name.png" alt="Matchlytics" />
                </footer>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </LayoutGroup>
  );
};

export default TournamentReport;
